import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Sparkles, ShieldAlert, AlertTriangle, Info, CheckCircle2, XCircle, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const usd = (v) => fmtUSD(v, { compact: true });
const SEV = {
  high: { cls: "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]", Icon: ShieldAlert },
  medium: { cls: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]", Icon: AlertTriangle },
  low: { cls: "bg-[#e0f2fe] text-[#0369a1] border-[#bae6fd]", Icon: Info },
};

function InvoiceCard({ item, onDecide }) {
  const [open, setOpen] = useState(item.verdict === "review");
  const [note, setNote] = useState("");
  const flagged = item.verdict === "review";
  return (
    <div className={`cs-card p-5 ${flagged && !item.decision ? "border-l-4 border-l-[#be185d]" : ""}`}
      data-testid={`close-invoice-${item.number}`}>
      <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => setOpen((o) => !o)}>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{item.customer}
            <span className="cs-mono text-[11px] text-muted-foreground font-normal ml-2">{item.number} · {item.state}</span>
          </div>
          <div className="cs-mono text-[11px] text-muted-foreground mt-0.5">
            {usd(item.total_usd)} · margin {item.margin_pct}%{item.mom ? ` · ${item.mom}` : ""}
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-semibold border ${
          flagged ? "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]" : "bg-[#dcfce7] text-[#15803d] border-[#bbf7d0]"
        }`} data-testid={`close-verdict-${item.number}`}>
          {flagged ? `${item.flags.length} flag${item.flags.length > 1 ? "s" : ""}` : "clean"}
        </span>
        {item.decision && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${item.decision.decision === "approved" ? "text-[#15803d]" : "text-[#b91c1c]"}`}
            data-testid={`close-decision-badge-${item.number}`}>
            {item.decision.decision === "approved" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {item.decision.decision === "approved" ? "Approved" : "Needs changes"}
          </span>
        )}
        <span className="ml-auto">{open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}</span>
      </div>

      {open && (
        <div className="mt-4 space-y-3">
          {item.flags.map((f, i) => {
            const S = SEV[f.severity];
            return (
              <div key={i} className="flex items-start gap-2.5" data-testid={`close-flag-${item.number}-${f.code}`}>
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wide border font-semibold inline-flex items-center gap-1 shrink-0 mt-0.5 ${S.cls}`}>
                  <S.Icon className="w-3 h-3" /> {f.severity}
                </span>
                <div>
                  <span className="text-xs font-semibold">{f.title}</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.detail}</p>
                </div>
              </div>
            );
          })}
          {!item.flags.length && <p className="text-xs text-muted-foreground">No flags — nothing a CFO would question.</p>}

          <div className="bg-[hsl(var(--cs-tint))] border border-border rounded-sm p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#00579e]" /> Drafted customer commentary
              </span>
              <button className="text-[11px] text-[#00579e] hover:underline inline-flex items-center gap-1" data-testid={`close-copy-${item.number}`}
                onClick={() => { navigator.clipboard.writeText(item.commentary); toast.success("Commentary copied"); }}>
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs leading-relaxed" data-testid={`close-commentary-${item.number}`}>{item.commentary}</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reviewer note (optional)"
              className="h-9 min-h-9 text-xs flex-1 max-w-md resize-none" data-testid={`close-note-${item.number}`} />
            <Button size="sm" className="h-8 bg-[#15803d] hover:bg-[#166534] text-white" data-testid={`close-approve-${item.number}`}
              onClick={() => onDecide(item.invoice_id, "approved", note)}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-[#b91c1c] hover:text-[#b91c1c]" data-testid={`close-reject-${item.number}`}
              onClick={() => onDecide(item.invoice_id, "needs_changes", note)}>
              <XCircle className="w-3.5 h-3.5 mr-1.5" /> Needs changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CloseCopilotTab({ period }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.close.review(period).then(setData).catch((e) => {
      setData(null);
      setError(e?.response?.data?.detail || e.message);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const decide = async (invoiceId, decision, note) => {
    try {
      await api.close.decide({ invoice_id: invoiceId, decision, note });
      toast.success(decision === "approved" ? "Invoice approved for close" : "Marked as needs changes");
      load();
    } catch (e) {
      toast.error("Failed to save decision", { description: e?.response?.data?.detail || e.message });
    }
  };

  if (error) {
    return <div className="cs-card p-8 text-sm text-muted-foreground" data-testid="close-empty">{error}</div>;
  }
  if (!data) {
    return <div className="cs-card p-8 text-sm text-muted-foreground" data-testid="close-loading">ASTRA is reviewing the period's invoices…</div>;
  }
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="close-copilot-tab">
      <div className="cs-card p-5 border-l-4" style={{ borderLeftColor: s.ready_to_close ? "#15803d" : "#be185d" }} data-testid="close-ai-summary">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#00579e] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">ASTRA Close Copilot · {data.period}</div>
            <p className="text-sm leading-relaxed">{data.ai_summary}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap" data-testid="close-summary-strip">
        {[["Invoices", s.invoices], ["Flagged", s.flagged], ["Clean", s.clean],
          ["Flagged Amount", usd(s.flagged_amount)], ["Awaiting Decision", s.undecided]].map(([l, v]) => (
          <div key={l} className="cs-card px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{l}</div>
            <div className="cs-mono text-lg font-semibold">{v}</div>
          </div>
        ))}
        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
          s.ready_to_close ? "bg-[#dcfce7] text-[#15803d] border-[#bbf7d0]" : "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]"
        }`} data-testid="close-ready-badge">
          {s.ready_to_close ? "✓ Ready to close" : "Not ready — decisions pending"}
        </span>
      </div>

      <div className="space-y-3">
        {data.items.map((item) => <InvoiceCard key={item.invoice_id} item={item} onDecide={decide} />)}
      </div>
    </div>
  );
}
